import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { ModalShellComponent } from '../modal-shell/modal-shell.component';
import { LoaderComponent } from '../../common/loader/loader.component';
import { TenantServiceDbService } from '../../services/tenant-service-db.service';
import { ToastrService } from '../../services/toastr.service';
import { MdmDbCredentials } from '../../models/tenant-service-db.model';
import { ApiErrorResponse } from '../../models/auth.model';

@Component({
  selector: 'app-tenant-service-mdm-db-config',
  imports: [ModalShellComponent, ReactiveFormsModule, LoaderComponent],
  templateUrl: './tenant-service-mdm-db-config.component.html',
  styleUrl: './tenant-service-mdm-db-config.component.scss'
})
export class TenantServiceMdmDbConfigComponent implements OnInit {
  @Input() tenantUid = '';
  @Input() preloadedConfig?: MdmDbCredentials | null;

  @Output() closed = new EventEmitter<void>();
  @Output() saved = new EventEmitter<void>();

  loading = true;
  saving = false;
  deleting = false;
  isEditing = false;
  confirmingDelete = false;
  showPassword = false;

  form!: FormGroup;

  constructor(
    private fb: FormBuilder,
    private tenantServiceDbService: TenantServiceDbService,
    private toastrService: ToastrService
  ) { }

  ngOnInit(): void {
    this.form = this.fb.group({
      host: ['', Validators.required],
      db: ['', Validators.required],
      user: ['', Validators.required],
      password: ['', Validators.required],
      port: [1433, [Validators.required, Validators.min(1)]],
      driver: ['{ODBC Driver 17 for SQL Server}', Validators.required]
    });

    if (this.preloadedConfig !== undefined) {
      this.applyExisting(this.preloadedConfig);
      return;
    }
    this.loadExisting();
  }

  get title(): string {
    return this.confirmingDelete ? 'Remove MDM Database Config' : this.isEditing ? 'MDM Database Configuration' : 'Configure MDM Database';
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.saving = true;

    const value = this.form.getRawValue();

    const request$ = this.isEditing
      ? this.tenantServiceDbService.updateMdmDbConfig(this.tenantUid, value)
      : this.tenantServiceDbService.createMdmDbConfig({ tenant_uid: this.tenantUid, ...value });

    request$.subscribe({
      next: () => {
        this.saving = false;
        this.saved.emit();
        this.closed.emit();
      },
      error: (err: HttpErrorResponse) => this.fail(err)
    });
  }

  confirmDelete(): void {
    this.confirmingDelete = true;
  }

  cancelDelete(): void {
    this.confirmingDelete = false;
  }

  runDelete(): void {
    this.deleting = true;

    this.tenantServiceDbService.deleteMdmDbConfig(this.tenantUid).subscribe({
      next: () => {
        this.deleting = false;
        this.saved.emit();
        this.closed.emit();
      },
      error: (err: HttpErrorResponse) => {
        this.deleting = false;
        this.confirmingDelete = false;
        this.fail(err);
      }
    });
  }

  close(): void {
    if (this.saving || this.deleting) {
      return;
    }
    this.closed.emit();
  }

  private loadExisting(): void {
    this.loading = true;

    this.tenantServiceDbService.getMdmDbConfig(this.tenantUid).subscribe({
      next: (config) => {
        this.loading = false;
        this.applyExisting(config);
      },
      error: (err: HttpErrorResponse) => {
        this.loading = false;
        this.isEditing = false;
        if (err.status !== 404) {
          this.toastrService.error('Unable to load existing MDM configuration.');
        }
      }
    });
  }

  private applyExisting(config: MdmDbCredentials | null): void {
    this.loading = false;

    if (!config) {
      this.isEditing = false;
      return;
    }

    this.isEditing = true;
    this.form.patchValue({
      host: config.host,
      db: config.db,
      user: config.user,
      password: config.password,
      port: config.port,
      driver: config.driver
    });
  }

  private fail(err: HttpErrorResponse): void {
    this.saving = false;
    const body = err.error as ApiErrorResponse | undefined;
    this.toastrService.error(body?.detail ?? body?.error?.message ?? this.firstFieldError(body) ?? 'Something went wrong. Please try again.');
  }

  private firstFieldError(body?: ApiErrorResponse): string | null {
    if (!body) {
      return null;
    }
    for (const key of Object.keys(body)) {
      if (key === 'detail' || key === 'error') {
        continue;
      }
      const value = (body as Record<string, unknown>)[key];
      if (Array.isArray(value) && value.length) {
        return `${key}: ${value[0]}`;
      }
      if (typeof value === 'string') {
        return `${key}: ${value}`;
      }
    }
    return null;
  }
}
